import React from "react";
import styled, { keyframes } from "styled-components";
import { X, Clock } from "lucide-react";
import { useLanguage } from "../contexts/language";

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 448px;
  border-radius: 16px;
  background-color: #fff;
  padding: 24px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border-top: 3px solid #c9a961;
  animation: ${fadeInScale} 0.25s ease-out;
  max-height: 90vh;
  overflow-y: auto;
  margin: 0 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  border: none;
  background: #f0f0f0;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #666;
  transition: background-color 0.15s;

  &:hover {
    background: #e0e0e0;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${(props) => (props.$isOpen ? "#e6f4ea" : "#fce8e6")};
  color: ${(props) => (props.$isOpen ? "#0f7a39" : "#a1152d")};
  margin-bottom: 4px;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => (props.$isOpen ? "#0f7a39" : "#a1152d")};
`;

const SubtitleText = styled.p`
  font-size: 13px;
  color: #777;
  margin: 0 0 20px 0;
`;

const DayList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DayRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 8px;
  background-color: ${(props) => (props.$isToday ? "rgba(201, 169, 97, 0.08)" : "transparent")};
  border-left: ${(props) => (props.$isToday ? "3px solid #c9a961" : "3px solid transparent")};
`;

const DayName = styled.span`
  font-size: 14px;
  font-weight: ${(props) => (props.$isToday ? 600 : 400)};
  color: ${(props) => (props.$isToday ? "#333" : "#555")};
  min-width: 100px;
`;

const SlotsText = styled.span`
  font-size: 13px;
  color: ${(props) => (props.$closed ? "#999" : "#333")};
  font-style: ${(props) => (props.$closed ? "italic" : "normal")};
  text-align: right;
`;

const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export const KitchenScheduleModal = ({ kitchenStatus, onClose }) => {
  const { language, t } = useLanguage();

  if (!kitchenStatus) return null;

  const { is_open, weekly_schedule, current_slot_close, next_opening, server_day } = kitchenStatus;

  const getDayName = (dayData) => {
    return language === "id" ? dayData.day_id : dayData.day_en;
  };

  const formatSlots = (slots) => {
    if (!slots || slots.length === 0) {
      return { text: language === "id" ? "Tutup" : "Closed", closed: true };
    }
    const text = slots.map((s) => `${s.open} - ${s.close}`).join(", ");
    return { text, closed: false };
  };

  const getSubtitle = () => {
    if (is_open && current_slot_close) {
      return `${t.kitchen.closesAt} ${current_slot_close}`;
    }
    if (!is_open && next_opening) {
      const dayName = language === "id" ? next_opening.day_id : next_opening.day_en;
      const isSameDay = next_opening.day_of_week === server_day;
      if (isSameDay) {
        return `${t.kitchen.opensAt} ${next_opening.open_time}`;
      }
      return `${t.kitchen.opensAt} ${dayName} ${next_opening.open_time}`;
    }
    if (!is_open) {
      return t.kitchen.closedToday;
    }
    return "";
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Header>
          <TitleRow>
            <Clock size={20} color="#c9a961" />
            <Title>{t.kitchen.operatingHours}</Title>
          </TitleRow>
          <CloseButton onClick={onClose}>
            <X size={16} />
          </CloseButton>
        </Header>

        <StatusBadge $isOpen={is_open}>
          <StatusDot $isOpen={is_open} />
          {is_open ? t.kitchen.open : t.kitchen.closed}
        </StatusBadge>

        <SubtitleText>{getSubtitle()}</SubtitleText>

        <DayList>
          {DISPLAY_ORDER.map((day) => {
            const dayData = weekly_schedule?.[day.toString()];
            if (!dayData) return null;
            const isToday = day === server_day;
            const { text, closed } = formatSlots(dayData.slots);

            return (
              <DayRow key={day} $isToday={isToday}>
                <DayName $isToday={isToday}>
                  {getDayName(dayData)}
                  {isToday && (
                    <span style={{ fontSize: "11px", color: "#c9a961", marginLeft: "6px" }}>
                      {language === "id" ? "Hari Ini" : "Today"}
                    </span>
                  )}
                </DayName>
                <SlotsText $closed={closed}>{text}</SlotsText>
              </DayRow>
            );
          })}
        </DayList>
      </ModalContent>
    </ModalOverlay>
  );
};
